import React, { useEffect, useMemo, useState } from 'react';

import useCurrentAccountId from 'hooks/useCurrentAccountId';
import STORE from 'store';

interface AccountChartPalettesContextType {
  palettes: ChartPalettes.AccountPalette[];
  paletteLookup: { [id: string]: ChartPalettes.AccountPalette | undefined };
  // The palette new Studio blocks start with
  defaultPalette: ChartPalettes.AccountPalette | undefined;
}

export const AccountChartPalettesContext =
  React.createContext<AccountChartPalettesContextType>({
    palettes: [],
    paletteLookup: {},
    defaultPalette: undefined,
  });

// The account's own chart palettes, made in Studio. Provided app-wide since
// charts link to them wherever they're drawn (dashboards, reports, popups).
// There are only ever a handful.
const AccountChartPalettesProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const accountId = useCurrentAccountId();
  const [palettes, setPalettes] = useState<ChartPalettes.AccountPalette[]>([]);

  useEffect(() => {
    let isActive = true;
    setPalettes([]);
    const unsubscribe = STORE.getChartPalettesRef({ accountId }).onSnapshot(
      (snapshot) => {
        if (!isActive) {
          return;
        }
        setPalettes(snapshot.docs.map((d) => d.data()));
      },
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [accountId]);

  const value = useMemo((): AccountChartPalettesContextType => {
    const sorted = [...palettes].sort((a, b) => a.name.localeCompare(b.name));
    const paletteLookup: AccountChartPalettesContextType['paletteLookup'] = {};
    sorted.forEach((palette) => {
      paletteLookup[palette.id] = palette;
    });
    return {
      palettes: sorted,
      paletteLookup,
      defaultPalette: sorted.find((p) => p.isDefault),
    };
  }, [palettes]);

  return (
    <AccountChartPalettesContext.Provider value={value}>
      {children}
    </AccountChartPalettesContext.Provider>
  );
};

export default AccountChartPalettesProvider;
